import U2000CheckBox from "../../../u2000Core/components/formControls/checkbox/U2000CheckBox";
import U2000InputText from "../../../u2000Core/components/formControls/input/U2000InputText";
import type { U2000FormStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { NS } from "../../U3142";
import U2000Panel from "../../../u2000Core/components/layout/U2000Panel";
import U2000InputCalendarCustom from "../../../u2000Core/components/formControls/input/U2000InputCalendarCustom";
import { useTranslation } from "react-i18next";
import { U3142LotStateEnum } from "../common/lotDetailStateTypes";
import U2000RadioGroupString from "../../../u2000Core/components/formControls/radio/U2000RadioGroupString";
import { U2000FormStateEnumState, U2000FormStateEnumEditMode } from "../../../u2000Core/reducer/formReducer/U2000FormState";
import {
  anyLotDetailOperationTypeHasSupplementary,
  buildLotDetailOperationTypeRadioOptions,
  getOperationTypeLabelsWithSupplementaryInfoEnabled,
  isLotDetailSupplementaryEnabledForOperationType,
} from "../../../u3141LandRegister/components/common/U3141LotOperationType";
import U2000InputTag, { U2000InputTagEnum } from "../../../u2000Core/components/formControls/input/U2000InputTag";
import U2000AutoCompleteInput from "../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput";
import { useCallback, useEffect, useRef, useState } from "react";
import { useU3142FindCadastresHandlers, useU3142FindDistrictsHandlers } from "./U3142UseFindDistrictCadastreHandlers";
import { createPortal } from "react-dom";
import { formatConcordanceEntryForDisplay } from "../../../u3141LandRegister/components/form/u3141ChildLotChain/U3141ConcordanceUtils";
import U3142ConcordanceDialog from "./U3142ConcordanceDialog";
import U2000Label from "../../../u2000Core/components/formControls/label/U2000Label";
import U2000InvalidFeedback from "../../../u2000Core/components/formControls/feedback/U2000InvalidFeedback";
import { FaPencilAlt } from "react-icons/fa";
import { useU3142Context } from "../../U3142Context";

// Typage des props du composant
interface U3142LotComponentProps<T extends Record<string, any>> {
  formStateHandlers: U2000FormStateHandlers<T>;
  t: any;
}

function U3142LotBody<T extends Record<string, any>>({ formStateHandlers, t }: U3142LotComponentProps<T>) {
  const { i18n } = useTranslation();
  const { unitInitLU } = useU3142Context();
  const opTypesFromInit = unitInitLU.lotDetailOperationTypes;
  const supplementaryFieldTypeLabels = getOperationTypeLabelsWithSupplementaryInfoEnabled(opTypesFromInit, i18n?.language || "fr");
  const findDistrictsHandlers = useU3142FindDistrictsHandlers();
  const findCadastresHandlers = useU3142FindCadastresHandlers({
    getDistrictId: () => formStateHandlers.getData()?.district?.id ?? null,
  });
  const [isConcordanceDialogOpen, setConcordanceDialogOpen] = useState(false);

  const setCadastreSelection = useRef<((item: any) => void) | null>(null);
  const setCadastreInputValue = useRef<((value: string) => void) | null>(null);

  const lotData = formStateHandlers.getData();
  const selectedOperationType = lotData?.operationType;
  const concordance = (lotData?.concordance ?? []) as string[];
  const concordanceOrigines = (lotData?.concordanceOrigines ?? []) as string[];

  useEffect(() => {
    const row = formStateHandlers.getData();
    if (!row || !opTypesFromInit?.length) return;
    if (!isLotDetailSupplementaryEnabledForOperationType(row.operationType, opTypesFromInit)) {
      const cur = row.operationTypeInfo;
      if (cur != null && String(cur).trim() !== "") {
        formStateHandlers.setData({ ...row, operationTypeInfo: null } as T);
      }
    }
  }, [selectedOperationType, opTypesFromInit, formStateHandlers]);
  const parentContext = useRef({
    codeDistrict: lotData?.district?.codeDistrict,
    cadastreCode: lotData?.cadastre?.cadastreCode,
  });
  parentContext.current = {
    codeDistrict: lotData?.district?.codeDistrict,
    cadastreCode: lotData?.cadastre?.cadastreCode,
  };

  /** Mise à jour du state formulaire uniquement ; persistance lors du save du lot (POST update/create), pas d’endpoint dédié (directive MA-91 §2.0). */
  const handleSaveConcordance = useCallback(
    (newConcordance: string[]) => {
      const current = formStateHandlers.getData();
      if (current) formStateHandlers.setData({ ...current, concordance: newConcordance });
      setConcordanceDialogOpen(false);
    },
    [formStateHandlers],
  );

  const onSearchDistrict = useCallback(
    async (criterias: { fieldName: string; value: any }[]) => {
      return (await findDistrictsHandlers.search(criterias)).results as Record<string, unknown>[];
    },
    [findDistrictsHandlers]
  );

  const displayDistrict = (item: any) => {
    if (item) {
      return `${item.propertyDistrict}`;
    } else {
      return "";
    }
  };

  // Extraction des données pour le champ district
  const extractDataForDistrict = (result: any) => {
    if (result) {
      return [
        { fieldName: "district.id", value: result.id },
        { fieldName: "district.propertyDistrict", value: result.propertyDistrict },
      ];
    } else {
      // Reset le contenu du champ cadastre
      setCadastreSelection.current?.(null);
      setCadastreInputValue.current?.("");

      return [
        { fieldName: "district.id", value: null },
        { fieldName: "district.propertyDistrict", value: null },
        { fieldName: "cadastre.id", value: null },
        { fieldName: "cadastre.cadastre", value: null },
        { fieldName: "cadastre.rankCode", value: null },
        { fieldName: "cadastre.rankLabel", value: null },
      ];
    }
  };

  const onSearchCadastre = useCallback(
    async (criterias: { fieldName: string; value: any }[]) => {
      if (!formStateHandlers.getData()?.district?.id) {
        return [];
      }
      return (await findCadastresHandlers.search(criterias)).results as Record<string, unknown>[];
    },
    [findCadastresHandlers, formStateHandlers]
  );

  const displayCadastre = (item: any) => {
    if (item) {
      return `${item.cadastre} ${item.rankLabel}`;
    } else {
      return "";
    }
  };

  // Extraction des données pour le champ cadastre
  const extractDataForCadastre = (result: any) => {
    if (result) {
      return [
        { fieldName: "cadastre.id", value: result.id },
        { fieldName: "cadastre.cadastre", value: result.cadastre },
        { fieldName: "cadastre.rankCode", value: result.rankCode },
        { fieldName: "cadastre.rankLabel", value: result.rankLabel },
      ];
    } else {
      return [
        { fieldName: "cadastre.id", value: null },
        { fieldName: "cadastre.cadastre", value: null },
        { fieldName: "cadastre.rankCode", value: null },
        { fieldName: "cadastre.rankLabel", value: null },
      ];
    }
  };

  // Réinitialiser le cadastre quand le district est effacé
  useEffect(() => {
    const district = formStateHandlers.getData()?.district;
    if (!district || !district.id || district.id < 0) {
      // Réinitialiser le cadastre
      setCadastreSelection.current?.(null);
      setCadastreInputValue.current?.("");
      const currentValue = formStateHandlers.getData();
      if (currentValue) {
        const newValue = { ...currentValue } as Record<string, any>;
        if (newValue.cadastre) {
          newValue.cadastre = null;
          formStateHandlers.setData(newValue as T);
        }
      }
    }
  }, [formStateHandlers.getData()?.district?.id]);

  return (
    <div className="flex h-full items-stretch gap-4">
      {/* Colonne gauche */}
      <div className="flex flex-[66_66_0%] flex-col gap-4">
        <U2000Panel title={t("lblGeneralInfo", { ns: NS })} titleAlign="left" className="flex h-full w-full flex-col items-start justify-start">
          <div className="mt-3 w-full">
            <U2000InputText
              formStateHandlers={formStateHandlers}
              fieldName="lot"
              label={t("lblLot", { ns: NS })}
              readonly={formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT}
              required
              inlineEnd={<U2000CheckBox formStateHandlers={formStateHandlers} fieldName="partieDeLot" label={t("lblPartieDeLot", { ns: NS })} />}
            />
          </div>

          <div className="mt-3 flex w-full gap-4">
            <div className="flex-1">
              <U2000AutoCompleteInput
                pageSize={15}
                formStateHandlers={formStateHandlers}
                fieldName="district"
                mainFieldName="propertyDistrict"
                label={t("lblDistrict", { ns: NS })}
                onSearch={onSearchDistrict}
                displayFn={displayDistrict}
                overrideExtractDataFn={extractDataForDistrict}
                basicResultHeight={300}
                minCarLaunchSearch={0}
                resultLabels={[]}
                resultFields={["propertyDistrict"]}
                required
              />
            </div>
          </div>

          <div className="mt-3 flex w-full gap-4">
            <div className="flex-1">
              <U2000AutoCompleteInput
                pageSize={15}
                formStateHandlers={formStateHandlers}
                fieldName="cadastre"
                mainFieldName="cadastre"
                label={t("lblCadastre", { ns: NS })}
                onSearch={onSearchCadastre}
                displayFn={displayCadastre}
                overrideExtractDataFn={extractDataForCadastre}
                basicResultHeight={300}
                minCarLaunchSearch={3}
                resultLabels={[]}
                resultFields={["cadastre", "rankLabel"]}
                onSetCurrentSelection={(setCurrentSelection, setInputValue) => {
                  setCadastreSelection.current = setCurrentSelection;
                  setCadastreInputValue.current = setInputValue;
                }}
                readonly={!formStateHandlers.getData()?.district?.id || formStateHandlers.getData()?.district?.id < 0}
                required
              />
            </div>
          </div>

          <div className="mt-3 flex w-full gap-4">
            <div className="flex-1">
              <U2000InputCalendarCustom formStateHandlers={formStateHandlers} fieldName="dateEstablishment" label={t("lblDateEstablishment", { ns: NS })} required />
            </div>
          </div>

          {/* Concordance : même présentation que U3141 (lecture seule + bouton ouvrant le popup d'édition) */}
          <div className="mt-3 flex w-full gap-4">
            <div className="flex-1 space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <U2000Label label={t("lblConcordance", { ns: NS })} />
                {formStateHandlers.getFormState() === U2000FormStateEnumState.EDIT && (
                  <button
                    type="button"
                    onClick={() => setConcordanceDialogOpen(true)}
                    className="inline-flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                    aria-label={t("btnModifyConcordances", { ns: NS })}
                  >
                    <FaPencilAlt className="w-4 h-4" />
                    <span>{t("btnModifyConcordances", { ns: NS })}</span>
                  </button>
                )}
              </div>
              <div
                className={`flex min-h-[46px] w-full flex-wrap items-center gap-1.5 rounded-lg border border-gray-300 px-4 py-2 text-sm ${formStateHandlers.getFormState() === U2000FormStateEnumState.READ ? "bg-muted" : "bg-muted/50"}`}
              >
                {Array.isArray(concordance) && concordance.length > 0 ? (
                  concordance.map((entry: string, idx: number) => {
                    const label = formatConcordanceEntryForDisplay(entry, parentContext.current);
                    const isReadOnly = formStateHandlers.getFormState() === U2000FormStateEnumState.READ;
                    const tagClasses = isReadOnly
                      ? "inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full transition-all duration-150 max-w-xs bg-muted text-gray-600 border border-gray-200"
                      : "inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full transition-all duration-150 max-w-xs bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100";
                    return (
                      <span key={`${idx}-${entry}`} className={tagClasses} title={label}>
                        {label}
                      </span>
                    );
                  })
                ) : (
                  <span className="text-gray-500 italic">{t("lblConcordanceEmptyHint", { ns: NS, defaultValue: "Aucune concordance" })}</span>
                )}
              </div>
              {typeof document !== "undefined" &&
                createPortal(
                  <U3142ConcordanceDialog isOpen={isConcordanceDialogOpen} onClose={() => setConcordanceDialogOpen(false)} concordance={concordance} onSave={handleSaveConcordance} />,
                  document.body,
                )}
              <U2000InvalidFeedback formStateHandlers={formStateHandlers} fieldName="concordance" />
            </div>
          </div>

          <div className="mt-3 flex w-full gap-4">
            <div className="flex-1">
              <U2000InputTag formStateHandlers={formStateHandlers} fieldName="corrections" label={t("lblCorrections", { ns: NS })} inputType={U2000InputTagEnum.DATE} />
            </div>
          </div>

          <div className="mt-5 flex w-full gap-4">
            <div className="flex-1">
              <U2000RadioGroupString
                titleGroup={t("lblOperationType", { ns: NS })}
                fieldName="operationType"
                orientation="horizontal"
                options={buildLotDetailOperationTypeRadioOptions(opTypesFromInit, i18n?.language || "fr", selectedOperationType)}
                formStateHandlers={formStateHandlers}
                readonly={formStateHandlers.getFormState() === U2000FormStateEnumState.READ}
                required
              >
                {anyLotDetailOperationTypeHasSupplementary(opTypesFromInit) ||
                isLotDetailSupplementaryEnabledForOperationType(selectedOperationType, opTypesFromInit) ? (
                  <U2000InputText
                    formStateHandlers={formStateHandlers}
                    fieldName="operationTypeInfo"
                    label={t("lblLotDetailSupplementaryInfo", { ns: NS, types: supplementaryFieldTypeLabels })}
                    readonly={formStateHandlers.getFormState() === U2000FormStateEnumState.READ || !isLotDetailSupplementaryEnabledForOperationType(selectedOperationType, opTypesFromInit)}
                  />
                ) : null}
              </U2000RadioGroupString>
            </div>
          </div>
        </U2000Panel>
      </div>

      {/* Colonne droite */}
      <div className="flex flex-[34_34_0%] flex-col gap-4">
        <U2000Panel title={t("lblAdditionalInfo", { ns: NS })} titleAlign="left" className="flex h-full w-full flex-col items-start justify-start">
          {/* Concordance origines : même logique d’affichage que U3141 (pastilles + formatConcordanceEntryForDisplay), lecture seule */}
          <div className="mt-3 flex w-full flex-col gap-1.5">
            <U2000Label label={t("lblConcordanceOrigines", { ns: NS })} />
            <div className="flex min-h-[46px] w-full flex-wrap items-center gap-1.5 rounded-lg border border-gray-300 bg-muted px-4 py-2 text-sm">
              {Array.isArray(concordanceOrigines) && concordanceOrigines.length > 0 ? (
                concordanceOrigines.map((entry: string, idx: number) => {
                  const label = formatConcordanceEntryForDisplay(entry, parentContext.current);
                  const tagClasses = "inline-flex max-w-xs items-center rounded-full border border-gray-200 bg-muted px-2.5 py-1 text-xs font-medium text-gray-600 transition-all duration-150";
                  return (
                    <span key={`${idx}-orig-${entry}`} className={tagClasses} title={label}>
                      {label}
                    </span>
                  );
                })
              ) : (
                <span className="text-gray-500 italic">{t("lblConcordanceOriginesEmpty", { ns: NS })}</span>
              )}
            </div>
          </div>

          <div className="mt-5 flex w-full gap-4">
            <div className="flex-1">
              {(() => {
                const lang = i18n?.language || "fr";
                const options = U3142LotStateEnum.map((type) => ({
                  value: type.value,
                  label: typeof type.label === "object" ? (type.label as Record<string, string>)[lang] : type.label,
                }));
                return (
                  <U2000RadioGroupString
                    titleGroup={t("lblLotState", { ns: NS })}
                    fieldName="lotState"
                    orientation="horizontal"
                    options={options}
                    formStateHandlers={formStateHandlers}
                    readonly={formStateHandlers.getFormState() === U2000FormStateEnumState.READ}
                    required
                  />
                );
              })()}
            </div>
          </div>

          <U2000Panel title={t("lblHistoricalChange", { ns: NS })} titleAlign="left" className="mt-5 flex h-full w-full flex-col items-start justify-start">
            <div className="mt-1 flex w-full flex-row gap-4">
              <div className="flex-1">
                <U2000InputText formStateHandlers={formStateHandlers} fieldName="createdByUser" label={t("lblCreatedByUser", { ns: NS })} readonly={true} />
              </div>
              <div className="flex-1">
                <U2000InputText formStateHandlers={formStateHandlers} fieldName="createdAt" label={t("lblFldCreatedAt", { ns: NS })} readonly={true} />
              </div>
            </div>

            <div className="mt-1 flex w-full flex-row gap-4">
              <div className="flex-1">
                <U2000InputText formStateHandlers={formStateHandlers} fieldName="lastModifiedByUser" label={t("lblLastModifiedByUser", { ns: NS })} readonly={true} />
              </div>
              <div className="flex-1">
                <U2000InputText formStateHandlers={formStateHandlers} fieldName="lastModifiedAt" label={t("lblLastModifiedAt", { ns: NS })} readonly={true} />
              </div>
            </div>
          </U2000Panel>
        </U2000Panel>
      </div>
    </div>
  );
}

export default U3142LotBody;
